import { motion } from 'framer-motion';
import { BellRing, CalendarClock, Lock, NotebookPen, UserRound } from 'lucide-react';
import { Progress } from '../ui/progress';
import { DeadlineText, StatusBadge } from './QualityBadges';
import { TaskItemRow } from './TaskItemRow';
import { SubmittedFeedback, TaskFeedbackForm } from './TaskFeedbackForm';
import { formatDate, isOpen, REVIEW_CYCLE_LABELS, scoreTone, timeAgo } from './qualityConfig';

const ReminderNote = ({ task }) => (
    <p className="flex items-center gap-2 rounded-lg bg-amber-400/15 px-3 py-2 text-sm text-amber-800 dark:text-amber-200" data-testid={`quality-task-reminder-${task.id}`}>
        <BellRing className="h-4 w-4 shrink-0" />
        {task.last_nudged_by_name} sent you a reminder {timeAgo(task.last_nudged_at)}{task.nudge_count > 1 ? ` (${task.nudge_count} so far)` : ''}.
    </p>
);

const ScoreBlock = ({ task }) => {
    const value = task.score ?? task.score_so_far;
    const caption = task.status === 'completed' ? 'task score' : task.status === 'missed' ? 'missed · score 0' : value === null ? 'no score yet' : 'score so far';
    return (
        <div className="text-right" data-testid={`quality-task-score-${task.id}`}>
            <p className={`font-heading text-4xl font-bold leading-none tabular-nums ${scoreTone(value)}`}>{value ?? '—'}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{caption}</p>
        </div>
    );
};

export const ReviewerNotes = ({ notes }) => (
    <div className="rounded-xl border-l-4 border-violet-500 bg-violet-500/5 px-4 py-3" data-testid="quality-task-notes">
        <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-violet-700 dark:text-violet-300">
            <NotebookPen className="h-3 w-3" /> Notes from your Quality reviewer
        </p>
        <p className="mt-1.5 whitespace-pre-line text-sm">{notes}</p>
    </div>
);

export const TaskCard = ({ task, index, onChanged }) => {
    const locked = task.status === 'missed';
    const pct = Math.round((task.completed_count / task.total_count) * 100);
    return (
        <motion.article
            layout
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`overflow-hidden rounded-2xl border bg-card ${locked ? 'opacity-80' : ''} ${task.due_soon ? 'ring-1 ring-amber-400/70' : ''}`}
            data-testid={`quality-task-card-${task.id}`}
        >
            <div className="h-1 bg-muted"><div className="h-full bg-violet-500 transition-all duration-700" style={{ width: `${pct}%` }} /></div>
            <div className="space-y-4 p-5">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 space-y-1.5">
                        <StatusBadge status={task.status} dueSoon={task.due_soon} testId={`quality-task-status-${task.id}`} />
                        <h3 className="font-heading text-lg font-semibold leading-tight">{task.title}</h3>
                        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1"><UserRound className="h-3 w-3" />{task.assigned_by_name}</span>
                            <span>{REVIEW_CYCLE_LABELS[task.review_cycle]} review</span>
                            <span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" />Due {formatDate(task.deadline)}</span>
                            <DeadlineText task={task} testId={`quality-task-timeleft-${task.id}`} />
                        </p>
                    </div>
                    <ScoreBlock task={task} />
                </div>
                <div className="flex items-center gap-3">
                    <Progress value={pct} className="h-1.5 flex-1" />
                    <span className="font-mono text-xs tabular-nums text-muted-foreground" data-testid={`quality-task-progress-${task.id}`}>{task.completed_count}/{task.total_count} done</span>
                </div>
                {isOpen(task) && task.last_nudged_at && <ReminderNote task={task} />}
                {task.call_feedback && <ReviewerNotes notes={task.call_feedback} />}
                {locked && (
                    <p className="flex items-center gap-2 rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300" data-testid={`quality-task-locked-${task.id}`}>
                        <Lock className="h-4 w-4" /> Deadline passed — this task is locked and scored 0. Ask your reviewer if you need more time.
                    </p>
                )}
                <ul className="space-y-2">{task.items.map((item) => <TaskItemRow key={item.item_id} taskId={task.id} item={item} locked={locked} requiredScore={task.required_score} onChanged={onChanged} />)}</ul>
                {task.status === 'completed' && (task.feedback
                    ? <SubmittedFeedback feedback={task.feedback} testId={`quality-feedback-submitted-${task.id}`} />
                    : <TaskFeedbackForm task={task} onSubmitted={onChanged} />)}
            </div>
        </motion.article>
    );
};
